import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Star, ArrowRight, X, Search, MessageSquare } from 'lucide-react';
import HeroSearchPill from './hero/HeroSearchPill';
import PhoneDemo from './hero/PhoneDemo';
import AppStorePromo from './AppStorePromo';
import './HeroPhone.css';

const BOOK_DEMO_URL = 'https://portal.versalaunch.com/book-demo';
const EASE = [0.22, 0.61, 0.36, 1];

const TERM_INFO = {
  seo: {
    label: 'SEO',
    title: 'SEO - Search Engine Optimization',
    tagline: 'Be the business Google shows first.',
    Icon: Search,
    why: 'Nearly every customer journey starts with a search, and the top three Google results take the overwhelming majority of clicks. If you’re not on page one for the services you sell, those customers are calling your competitors - they never even know you exist.',
    what: [
      'Our custom software audits your site continuously - speed, mobile experience, and technical health - and flags anything holding your rankings back.',
      'We build structured data (schema) into every page so Google understands exactly what you do and where you do it.',
      'We optimize your Google Business Profile, local citations, and reviews so you dominate the map pack in your service area.',
      'Rank tracking is built into your dashboard, so you watch yourself climb for the keywords that actually bring in jobs.'
    ]
  },
  aeo: {
    label: 'AEO',
    title: 'AEO - Answer Engine Optimization',
    tagline: 'Be the answer AI gives.',
    Icon: MessageSquare,
    why: 'Customers are skipping the list of links. They ask ChatGPT, Siri, and Google’s AI Overviews a question and take the one answer they get back. If AI engines can’t read and trust your business, you’re invisible in the fastest-growing way people find local services.',
    what: [
      'Our custom software structures your business data - services, pricing, areas, FAQs - in the machine-readable formats AI engines pull answers from.',
      'We publish question-and-answer content that mirrors how real customers actually ask, so AI assistants cite you as the answer.',
      'We keep your business entity consistent across the web, which is how AI models decide who to recommend.',
      'As answer engines evolve, our platform updates your markup and content automatically - you stay ahead without lifting a finger.'
    ]
  }
};

/* Popup explaining SEO / AEO and how our custom software gets you to the top. */
const TermModal = ({ term, onClose }) => {
  const overlayRef = useRef(null);
  const info = TERM_INFO[term];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!info) return null;
  const { Icon } = info;

  // Portal to <body>: ancestor transforms (page-transition wrappers) would
  // otherwise hijack position:fixed and strand the overlay mid-document.
  return createPortal(
    <div
      className="hero-term-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <motion.div
        className="hero-term-modal"
        role="dialog"
        aria-modal="true"
        aria-label={info.title}
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 10 }}
        transition={{ duration: 0.25, ease: EASE }}
      >
        <div className="hero-term-head">
          <div className="hero-term-head-text">
            <span className="hero-term-badge"><Icon size={14} strokeWidth={2.4} /> {info.label}</span>
            <h2>{info.title}</h2>
            <p>{info.tagline}</p>
          </div>
          <button type="button" className="hero-term-close" onClick={onClose} aria-label="Close">
            <X size={16} strokeWidth={2.4} />
          </button>
        </div>
        <div className="hero-term-body">
          <h3>Why it matters</h3>
          <p>{info.why}</p>
          <h3>How we get you to the top</h3>
          <ul>
            {info.what.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <a href={BOOK_DEMO_URL} className="hero-term-cta">
            See it in action - free demo
            <ArrowRight size={15} strokeWidth={2.4} />
          </a>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

const HeroPhone = () => {
  const reduce = useReducedMotion();
  const [openTerm, setOpenTerm] = useState(null);

  // Reading-order load-in. Each row rises a touch later than the last.
  const rise = (i) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.6, delay: 0.05 + i * 0.07, ease: EASE }
        };

  return (
    <section className="vlh-hero" aria-label="Versa Launch: get found, capture leads, automate and grow">
      <div className="hero-inner">
        <motion.p className="hero-trust" {...rise(0)}>
          <span className="hero-stars" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
            ))}
          </span>
          Trusted by 100+ local businesses<span className="hero-trust-extra"> in Jacksonville &amp; Ponte Vedra</span>
        </motion.p>

        <motion.h1 className="vlh-title" {...rise(1)}>
          Your business technology.
          <br />
          <span className="vlh-title-accent">One company.</span>
        </motion.h1>

        <motion.p className="hero-sub" {...rise(2)}>
          Websites. Phones. AI. Software.{' '}
          <button type="button" className="hero-sub-term" onClick={() => setOpenTerm('seo')}>
            SEO
          </button>
          {' / '}
          <button type="button" className="hero-sub-term" onClick={() => setOpenTerm('aeo')}>
            AEO
          </button>
          . Support.
        </motion.p>

        <motion.p className="hero-lede" {...rise(3)}>
          Versa Launch builds and manages the technology businesses rely on every day, from websites
          and phone systems to AI reception, software, search visibility, automation and ongoing
          support.
        </motion.p>

      </div>

      <div className="hero-cta-wrap">
        <motion.div className="hero-cta" {...rise(4)}>
          <HeroSearchPill />
        </motion.div>

        <motion.p className="hero-secondary" {...rise(5)}>
          Prefer a walkthrough?{' '}
          <a href={BOOK_DEMO_URL} className="hero-secondary-link">
            See Versa in action
            <ArrowRight size={15} strokeWidth={2.4} />
          </a>
        </motion.p>
      </div>

      <motion.div
        className="hero-stage-mount"
        initial={reduce ? false : { opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: reduce ? 0 : 0.42, ease: EASE }}
      >
        <PhoneDemo aside={<AppStorePromo variant="stage" />} />
      </motion.div>

      {/* Tablet/phone: compact App Store bar sits at the foot of the hero, right
          above the partners strip. Desktop hides this and uses the card floating
          in the gold beside the phone (see PhoneDemo aside). */}
      <AppStorePromo variant="bar" />

      <AnimatePresence>
        {openTerm && <TermModal term={openTerm} onClose={() => setOpenTerm(null)} />}
      </AnimatePresence>
    </section>
  );
};

export default HeroPhone;
